import { useTranslation } from 'react-i18next';
import type { LeaderboardEntry } from '../api';
import { useAuth } from '../auth';
import { humanDuration } from '../lib/format';

/**
 * Rangliste einer Herausforderung. Die kompakte Variante lässt die
 * Status-Spalte weg und faltet den Status in die Zeit-Spalte – sie steht neben
 * dem Fortschritt auf der Flugseite, wo wenig Platz ist.
 */
export function Leaderboard({
  entries,
  compact = false,
}: {
  entries: LeaderboardEntry[];
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();

  if (entries.length === 0) {
    return <p className="muted" style={{ margin: 0 }}>{t('challenge.empty')}</p>;
  }

  const pad = compact ? '6px' : '8px';

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <th style={{ padding: '6px 8px' }}>{t('challenge.rank')}</th>
            <th style={{ padding: '6px 8px' }}>{t('challenge.player')}</th>
            <th style={{ padding: '6px 8px' }}>{t('challenge.time')}</th>
            <th style={{ padding: '6px 8px' }}>{t('challenge.flights')}</th>
            {!compact && <th style={{ padding: '6px 8px' }}>{t('challenge.status')}</th>}
          </tr>
        </thead>
        <tbody className="mono" style={{ fontSize: compact ? '0.82rem' : '0.9rem' }}>
          {entries.map((e, i) => {
            const dur = humanDuration(e.totalDurationMinutes);
            const isOwn = e.ownerId === user?.id;
            const finished = e.status === 'finished';
            return (
              <tr
                key={e.roundId}
                data-testid="leaderboard-row"
                style={{ borderTop: '1px solid var(--line)', background: isOwn ? 'var(--go-soft)' : undefined }}
              >
                <td style={{ padding: pad, fontWeight: 700 }}>{finished ? i + 1 : '–'}</td>
                <td style={{ padding: pad }}>{e.ownerName}{isOwn && ` (${t('challenge.you')})`}</td>
                <td style={{ padding: pad }}>
                  {finished ? (
                    t('common.duration', { h: dur.h, m: dur.m })
                  ) : compact ? (
                    <span className="muted">{t(`directory.status.${e.status}`)}</span>
                  ) : (
                    '–'
                  )}
                </td>
                <td style={{ padding: pad }}>{e.flights}</td>
                {!compact && (
                  <td style={{ padding: pad }}>
                    <span className={`pill ${finished ? 'pill-go' : 'pill-neutral'}`}>
                      {t(`directory.status.${e.status}`)}
                    </span>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
